import React from 'react';
import FieldTypesEnum from '../Enums/FieldTypesEnum';
import dateTimeHelper from '../Helpers/datetimeHelper';
import moneyHelper from '../Helpers/moneyHelper';
import DataTableActionMenu from '../Elements/DataTable/DataTableActionMenu';

export default class OutsourcedFeeItem {
  constructor(item) {
    if (item === undefined) {
      this.OutsourcedFeeId = null;
      this.ThirdPartySource = '';

      this.ClientId = null;
      this.ClientName = '';
      this.SassieClientId = '';
      this.ThirdPartyClientId = '';

      this.SurveyId = null;
      this.SurveyName = '';
      this.SassieSurveyId = '';

      this.TpSurveyId = null;
      this.TpSurveyName = '';
      this.ThirdPartySurveyId = '';

      this.WaveId = null;
      this.WaveName = '';
      this.SassieWaveId = null;

      this.TpWaveId = null;
      this.TpWaveName = '';
      this.ThirdPartyWaveId = '';

      // Seed both dates: CaspianDatePicker DISPLAYS today when its value is null but never reports it, so a
      // null leaves a required date field looking filled while validation calls it empty and Finish stays
      // disabled. A new fee runs from today until someone closes it (the Close action sets the end date),
      // hence the indefinite end — same defaults as the vendor-side FreelancerFeeItem.
      this.StartDate = new Date();
      this.EndDate = dateTimeHelper.indefiniteDateValue();

      this.FieldworkOfficeId = null;
      this.FieldworkOfficeName = '';
      this.OutsourcingOfficeId = null;
      this.OutsourcingOfficeName = '';

      this.FeeAmount = null;
      this.CurrencyCode = '';
      this.FeeType = '';
      this.HasConnectedJobLinks = false;
    } else {
      this.initialize(item);
    }
  }

  initialize(item) {
    this.OutsourcedFeeId = item.outsourcedFeeId;
    this.ThirdPartySource = item.thirdPartySource ?? '';

    this.ClientId = item.clientId;
    this.ClientName = item.clientName ?? '';
    this.SassieClientId = item.sassieClientId ?? '';
    this.ThirdPartyClientId = item.thirdPartyClientId ?? '';

    this.SurveyId = item.surveyId;
    this.SurveyName = item.surveyName ?? '';
    this.SassieSurveyId = item.sassieSurveyId ?? '';

    this.TpSurveyId = item.tpSurveyId;
    this.TpSurveyName = item.tpSurveyName ?? '';
    this.ThirdPartySurveyId = item.thirdPartySurveyId ?? '';

    this.WaveId = item.waveId;
    this.WaveName = item.waveName ?? '';
    this.SassieWaveId = item.sassieWaveId ?? '';

    this.TpWaveId = item.tpWaveId;
    this.TpWaveName = item.tpWaveName ?? '';
    this.ThirdPartyWaveId = item.thirdPartyWaveId ?? '';

    this.StartDate = item.startDate;
    this.EndDate = item.endDate;

    this.FieldworkOfficeId = item.fieldworkOfficeId;
    this.FieldworkOfficeName = (item.fieldworkOfficeName ?? '').replace('BARE ', '').trim();
    this.OutsourcingOfficeId = item.outsourcingOfficeId;
    this.OutsourcingOfficeName = (item.outsourcingOfficeName ?? '').replace('BARE ', '').trim();

    this.FeeAmount = item.feeAmount;
    this.CurrencyCode = item.currencyCode ?? '';
    this.FeeType = item.feeType ?? '';
    this.HasConnectedJobLinks = item.hasConnectedJobLinks ?? false;
  }

  static NameOfTheIdField() {
    return 'OutsourcedFeeId';
  }

  static nameOfTheItemDetailList(item) {
    // No id in the caption: the fee's is a GUID, which means nothing to a user (CD-5079).
    return 'Outsourced Fee';
  }

  static MoneyFields = ['FeeAmount'];

  static Currency() {
    return 'CurrencyCode';
  }

  static MoneyFieldsSecondary = [];

  static CurrencySecondary() {
    return '';
  }

  static DateFields = ['StartDate', 'EndDate'];

  static BoolFields = [];

  static FieldsToShowInListTable = [
    {
      tabName: 'General',
      tabLabel: 'General',
      icon: 'fa-solid fa-home',
      data: [
        { name: '-message-', label: 'General Information', className: 'text-left' },

        // Work Done By before Bill To Office, as on every other outsourced surface (CD-5079).
        { name: 'OutsourcingOfficeName', label: 'Work Done By' },
        { name: 'FieldworkOfficeName', label: 'Bill To Office' },

        {
          name: (item) => moneyHelper.toMoneyFormat(item.FeeAmount, item.CurrencyCode),
          label: 'Fee',
        },
        { name: 'FeeType', label: 'Fee Type' },
        { label: '', name: '', className: 'text-left' },

        { name: (item) => dateTimeHelper.toYYYYsMMsDD(item.StartDate), label: 'Start Date' },
        // WithIndefinite: an open-ended fee stores 9999-12-31, which the plain formatter would print as a date.
        { name: (item) => dateTimeHelper.toYYYYsMMsDDWithIndefinite(item.EndDate), label: 'End Date' },

        { name: '-message-', label: 'Third Party Details', className: 'text-left' },

        // Every id is paired with its Bravo counterpart here, because the grid columns show only the Bravo id
        // now - this panel is where someone goes to find the Apollo or third-party one (CD-5079).
        { name: 'ThirdPartySource', label: 'Source' },
        { name: 'ClientName', label: 'Client Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'ClientId', label: 'Client Bravo ID' },
        { name: 'SassieClientId', label: 'Apollo Client ID' },
        { name: 'ThirdPartyClientId', label: 'Third Party Client ID' },

        { name: 'SurveyName', label: 'Survey Name' },
        { name: 'TpSurveyName', label: 'TP Survey Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'SurveyId', label: 'Survey Bravo ID' },
        { name: 'SassieSurveyId', label: 'Apollo Survey ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'TpSurveyId', label: 'TP Survey Bravo ID' },
        { name: 'ThirdPartySurveyId', label: 'Third Party Survey ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'WaveName', label: 'Wave Name' },
        { name: 'TpWaveName', label: 'TP Wave Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'WaveId', label: 'Wave Bravo ID' },
        { name: 'SassieWaveId', label: 'Apollo Wave ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'TpWaveId', label: 'TP Wave Bravo ID' },
        { name: 'ThirdPartyWaveId', label: 'Third Party Wave ID' },
        { label: '', name: '', className: 'text-left' },
      ],
    },
  ];

  // The id in brackets is always the BRAVO id. It used to be the third-party one (Apollo's, via the client's
  // SassieId, whenever the fee had no TpSurvey), which is what users were reading as a Bravo id and looking up
  // in vain (CD-5079). Both third-party ids are still on the details panel, each labelled for its system.
  static getClientDisplay(item) {
    return item.ClientId ? `${item.ClientName} (${item.ClientId})` : item.ClientName;
  }

  static getSurveyDisplay(item) {
    if (item.TpSurveyId) {
      return `${item.TpSurveyName} (${item.TpSurveyId})`;
    }
    if (item.SurveyId) {
      return `${item.SurveyName} (${item.SurveyId})`;
    }
    return '';
  }

  static getFeeDisplay(item) {
    return item.FeeAmount != null ? moneyHelper.toMoneyFormat(item.FeeAmount, item.CurrencyCode) : '';
  }

  static FieldsToShowInDataTable() {
    return [
      {
        // Carries the details link instead of the fee's GUID, which was unreadable and meaningless to
        // users. It has to be the row's position rather than a business column (client, office, fee
        // type, dates) because every one of those repeats across rows, and the link column needs to be
        // unique on screen. No orderById: a positional counter is not sortable.
        label: 'Page Position',
        orderById: '',
        data: 'PagePosition',
        type: FieldTypesEnum.DataFieldWithDetails,
        minWidth: 50,
      },
      {
        label: 'Client Name (Bravo ID)',
        orderById: 'ClientName',
        data: OutsourcedFeeItem.getClientDisplay,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Source',
        orderById: 'ThirdPartySource',
        data: 'ThirdPartySource',
        type: FieldTypesEnum.PureDataField,
        minWidth: 60,
      },
      {
        label: 'Survey (Bravo ID)',
        orderById: 'SurveyName',
        data: OutsourcedFeeItem.getSurveyDisplay,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Start Date',
        orderById: 'StartDate',
        data: (item) => dateTimeHelper.toYYYYsMMsDD(item.StartDate),
        type: FieldTypesEnum.PureDataField,
        minWidth: 80,
      },
      {
        label: 'End Date',
        orderById: 'EndDate',
        data: (item) => dateTimeHelper.toYYYYsMMsDDWithIndefinite(item.EndDate),
        type: FieldTypesEnum.PureDataField,
        minWidth: 80,
      },
      {
        // Work Done By before Bill To Office, as on every other outsourced surface (CD-5079).
        label: 'Work Done By',
        orderById: 'OutsourcingOfficeName',
        data: 'OutsourcingOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Bill To Office',
        orderById: 'FieldworkOfficeName',
        data: 'FieldworkOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Fee',
        orderById: 'FeeAmount',
        data: OutsourcedFeeItem.getFeeDisplay,
        type: FieldTypesEnum.PureDataField,
        minWidth: 60,
      },
      {
        label: 'Fee Type',
        orderById: 'FeeType',
        data: 'FeeType',
        type: FieldTypesEnum.PureDataField,
        minWidth: 80,
      },
      {
        label: ' ',
        orderById: '',
        data: OutsourcedFeeItem.getActionsContent,
        type: FieldTypesEnum.ActionButton,
        className: 'text-center',
      },
    ];
  }

  static getActionsContent(item) {
    return (
      <DataTableActionMenu
        options={[
          {
            iconName: 'fa-solid fa-edit',
            text: 'Edit',
            roleKey: 'outsourced_fees_edit',
            disabled: item.HasConnectedJobLinks,
            onClick: () => OutsourcedFeeItem.editFunction(item.OutsourcedFeeId, item),
          },
          {
            iconName: 'fa-regular fa-clone',
            text: 'Clone',
            roleKey: 'outsourced_fees_edit',
            onClick: () => OutsourcedFeeItem.cloneFunction(item.OutsourcedFeeId, item),
          },
          {
            iconName: 'fa-regular fa-stop-circle',
            text: 'Close',
            roleKey: 'outsourced_fees_edit',
            onClick: () => OutsourcedFeeItem.closeFunction(item.OutsourcedFeeId, item),
          },
        ]}
      />
    );
  }

  static editFunction(id, item) {
    //placeholder for a function from OutsourcedFeeAdmin.js - Do not delete
  }

  static cloneFunction(id, item) {
    //placeholder for a function from OutsourcedFeeAdmin.js - Do not delete
  }

  static closeFunction(id, item) {
    //placeholder for a function from OutsourcedFeeAdmin.js - Do not delete
  }
}
